import { FC, useEffect, useState } from "react";
import {
  IInitialStateProduct,
  iInitialStateProduct,
  IProductFieldProps,
} from "../../../Interfaces/Product/IProduct";
import { useSelector } from "react-redux";
import {
  FIELDS
} from "../../../Interfaces/Product/IProductFormField";
import store from "../../../Store/store";
import { editTablePayload } from "../../../Redux/Actions/editTableActions";
import { storeAddPayload, storeSaveResponse } from "../../../Redux/Actions/popupActions";
import { Container } from "@sainsburys-tech/luna-grid";
import { Card } from "@sainsburys-tech/luna-card";
import ProductEditForm from "./productEditForm";
import { storePageDirtyPayload } from "../../../Redux/Actions/pageDirtyAction";
import { validateForm, validateWarnings } from "./productValidations";
import { consolelog } from "../../../helpers/consolelog";
import { ProductValueType } from "../../../Types/Product/ProductValueType";
import { endpoints } from "../../../ApiEndPoints/apiEndPoints";
import { useGetApi } from "../../../Redux/Hooks/useGetApi";
import { UI_CONSTANTS } from "../../../Constants/constants";

const ProductFields: FC<IProductFieldProps> = ({ closeModal, isEdit }) => {
  const payload = useSelector((state: any) => state.popupReducer);
  const filter = useSelector((state: any) => state.filterReducer.data);
  const nonDirectQueryRespnse = useSelector((state: any) => state.menuHeaderReducer?.data);
  const saveResponse = useSelector((state: any) => state.popupReducer?.saveStatus);
  const [state, setState] = useState<IInitialStateProduct>(
    payload.data?.editPayload ? payload.data.editPayload : iInitialStateProduct
  );
  const [siblingPackValue, setSiblingPackValue] = useState<ProductValueType>();
  const [hasRunInitialQuery, setHasRunInitialQuery] = useState<boolean>(false);

  useEffect(() => {
    if (isEdit && saveResponse?.status === "success") {
      closeModal();
    }
  }, [saveResponse]);

  const siblingPackParams = {
    service: endpoints.getProductData(),
    skip: 0,
    take: 10,
    isEnabled:
      isEdit &&
      payload?.data?.selectedMenu === "Measurements" &&
      payload.data?.editPayload?.values?.sainId &&
      payload.data?.editPayload?.values?.alias == UI_CONSTANTS.ARGOS &&
      payload.data?.editPayload?.values?.packSize > 1 &&
      !hasRunInitialQuery,
    filter: payload.data?.editPayload?.values?.sainId
      ? encodeURIComponent(
          `tolower(SainId) eq '${payload.data?.editPayload?.values?.sainId?.toLowerCase()}' and Packsize eq 1`
        )
      : "",
    refetchOnWindowFocus: false,
    isPaginationRequired: true,
  };

  // API call to fetch sibling pack data
  const {
    data: siblingPackResponse,
    isLoading: siblingPackLoading,
    isSuccess: siblingPackSuccess,
  } = useGetApi(siblingPackParams);

    // Effect to process sibling pack data on first load
  useEffect(() => {
    if (
      siblingPackSuccess &&
      siblingPackResponse &&
      !siblingPackLoading &&
      !hasRunInitialQuery
    ) {
      consolelog("Sibling pack data:", siblingPackResponse);
      if (siblingPackResponse?.data?.value?.length > 0) {
        // Process the sibling pack data here
        const siblingData = siblingPackResponse.data.value[0];
        setSiblingPackValue(siblingData);
      }
      setHasRunInitialQuery(true);
    }
  }, [
    siblingPackResponse,
    siblingPackLoading,
    siblingPackSuccess,
    hasRunInitialQuery,
  ]);
  
  useEffect(() => {
    if (payload.data) {
      setState(payload.data.editPayload);
    } else {
      setState(iInitialStateProduct);
    }
  }, [payload.data]);

  useEffect(() => {
    if (state.values.sainId !== undefined) {
      store.dispatch(storePageDirtyPayload(state));
    }
  }, [state]);

  /*Rules and validation mechanism go here*/
  /*Validations*/

  const handleBlur = (target: any) => {
    const { values, errors } = state;

    const valid = validateForm(
      values,
      payload?.data?.selectedMenu,
      siblingPackValue
    );

    setState((initialState: IInitialStateProduct) => {
      return {
        ...initialState,
        errors: { ...errors, ...valid.errors },
      };
    });

    // For Warnings
    if (
      target?.name === FIELDS.outerDimensions.length.name ||
      target?.name === FIELDS.outerDimensions.width.name ||
      target?.name === FIELDS.outerDimensions.lengthUOM.name ||
      target?.name === FIELDS.outerDimensions.widthUOM.name
    ) {
      let fieldName: string;

      switch (target.name) {
        case FIELDS.outerDimensions.lengthUOM.name: {
          fieldName = FIELDS.outerDimensions.length.name;
          break;
        }
        case FIELDS.outerDimensions.widthUOM.name: {
          fieldName = FIELDS.outerDimensions.width.name;
          break;
        }
        default: {
          fieldName = target.name;
        }
      }

      const validateWarning = validateWarnings(fieldName, values, errors);
      const fieldNameArray = fieldName.split("_");

      setState((initialState: IInitialStateProduct) => {
        return {
          ...initialState,
          warnings: {
            ...initialState.warnings,
            outerDimensions: {
              ...initialState.warnings.outerDimensions,
              [fieldNameArray[1]]: validateWarning?.warningMessage,
            },
          },
        };
      });
    }
  };

  const setDefaultValueOnCheck = (target: any) => {
    switch (target?.name) {
      case FIELDS.breakPackIndicator.name: {
        setState((initialState: IInitialStateProduct) => {
          return {
            ...initialState,
            values: {
              ...initialState.values,
              outerDimensions: {
                ...initialState.values.outerDimensions,
                ti: 0,
                hi: 0,
                height: 0,
                weight: 0,
                width: 0,
                length: 0,
                breakPackQty: 0,
                volume: 0,
              },
            },
            errors: { ...initialState.errors, outerDimensions: {} },
            warnings: { ...initialState.warnings, outerDimensions: {} },
          };
        });
        break;
      }
      case FIELDS.hazardous.name: {
        setState((initialState: IInitialStateProduct) => {
          return {
            ...initialState,
            values: {
              ...initialState.values,
              hazardousCode: "",
            },
            errors: {
              ...initialState.errors,
              hazardousCode: undefined,
            },
            warnings: {
              ...initialState.warnings,
              hazardousCode: undefined,
            },
          };
        });
        break;
      }
      case FIELDS.nestedItem.name: {
        setState((initialState: IInitialStateProduct) => {
          return {
            ...initialState,
            values: {
              ...initialState.values,
              nestIncrement: "",
            },
            errors: {
              ...initialState.errors,
              nestIncrement: undefined,
            },
            warnings: {
              ...initialState.warnings,
              nestIncrement: undefined,
            },
          };
        });
        break;
      }
    }
  };

  const handleChange = (
    target: any,
    familygroup: string,
    description: string,
    isCheckBox: boolean
  ) => {
    if (
      familygroup !== undefined &&
      description !== undefined &&
      familygroup !== "" &&
      description !== "" &&
      !isCheckBox
    ) {
      setState((initialState: IInitialStateProduct) => {
        let data = {
          ...initialState,
          values: {
            ...initialState.values,
            familyGroupNew: familygroup,
          },
          dirty: true,
        };
        return data;
      });
    } else if (isCheckBox) {
      setDefaultValueOnCheck(target);
      setState((initialState: IInitialStateProduct) => {
        let data = {
          ...initialState,
          values: { ...initialState.values, [target.name]: target.checked },
          dirty: true,
        };
        return data;
      });
    } else {
      let fieldName = target.name.split("_");

      if (fieldName?.length === 2) {
        setState((initialState: IInitialStateProduct) => {
          if (fieldName[0] === "outerDimensions") {
            let data = {
              ...initialState,
              values: {
                ...initialState.values,
                outerDimensions: {
                  ...initialState.values.outerDimensions,
                  [fieldName[1]]: target.value,
                },
              },
              dirty: true,
            };
            return data;
          } else {
            let data = {
              ...initialState,
              values: {
                ...initialState.values,
                innerDimensions: {
                  ...initialState.values.innerDimensions,
                  [fieldName[1]]: target.value,
                },
              },
              dirty: true,
            };
            return data;
          }
        });
      } else {
        setState((initialState: IInitialStateProduct) => {
          let data = {
            ...initialState,
            values: { ...initialState.values, [target.name]: target.value },
            dirty: true,
          };
          return data;
        });
      }
    }
    //On change of Product we're making family group null to force user to search again
    if (target !== undefined && target.name === FIELDS.productType.name) {
      setState((initialState: IInitialStateProduct) => {
        let data = {
          ...initialState,
          values: { ...initialState.values, [FIELDS.familyGroup.name]: "" },
          dirty: true,
        };
        return data;
      });
    }
  };

  const { values, errors, submitted, dirty, warnings } = state;

  const handleSubmit = (e: any) => {
    const { values } = state;

    e.preventDefault();

    const valid = validateForm(
      values,
      nonDirectQueryRespnse?.selectedMenu || filter.selectedMenu,
      siblingPackValue
    );

    setState((initialState: IInitialStateProduct) => {
      return {
        ...initialState,
        errors: { ...errors, ...valid.errors },
        submitted: valid.result,
      };
    });

    if (!valid.result) {
      setTimeout(scrollToError, 100);
      return;
    }

    if (isEdit) {
      let familyGroup = values.familyGroupNew
        ? values.familyGroupNew
        : values.familyGroup;
      const editPayload = {
        Id: payload.data.id,
        SainId: values.sainId,
        ProductType: values.productType,
        SkuNumber: values.skuNumber,
        FamilyGroup: familyGroup,
        Ti: values.ti,
        Hi: values.hi,
        DepotId: values.depotId,
        PackSize: values.packSize,
        BreakPack: values.breakPack,
        EAN: values.ean,
        OuterWeight: values.outerWeight,
        OuterLength: values.outerLength,
        OuterWidth: values.outerWidth,
        OuterHeight: values.outerHeight,
        OldFamilyGroup: values.familyGroupNew
          ? values.familyGroup
          : values.oldFamilyGroup,
        Hazardous: values.hazardous,
        HazardousCode: values.hazardousCode ? values.hazardousCode : null,
        Conveyable: values.conveyable,
        Lifecycle: values.lifecycle,
        NestedItem: values.nestedItem,
        NestedIncrement: values.nestIncrement ? values.nestIncrement : null,
        MLOI: values.mloi,
        MLOR: values.mlor,
        Uglies: values.uglies,
        TwoPersonLift: values.twoPersonLift,
        ExpiryDateRequired: values.expiryDateRequired,
        PickPack: values.pickPack,
        Description: values.description,
        FamilyGroupNew: values.familyGroupNew,  
        BreakPackIndicator: values.breakPackIndicator,
        MuType: values.muType ? values.muType : null,
        AutomationAdaptoSystem: values.automationAdaptoSystem
          ? values.automationAdaptoSystem
          : null,
        AutomationToteFill: values.automationToteFill
          ? values.automationToteFill
          : null,
        AutomationMinTotes: values.automationMinTotes
          ? values.automationMinTotes
          : null,
        OuterDimensions: {
          Height: values.outerDimensions?.height,
          HeightUOM: values?.outerDimensions?.heightUOM,
          Width: values.outerDimensions?.width,
          WidthUOM: values.outerDimensions?.widthUOM,
          Length: values.outerDimensions?.length,
          LengthUOM: values.outerDimensions?.lengthUOM,
          Weight: values.outerDimensions?.weight,
          WeightUOM: values.outerDimensions?.weightUOM,
          Volume: values.outerDimensions?.volume,
          VolumeUOM: values.outerDimensions?.volumeUOM,
          Ti: values?.outerDimensions?.ti,
          Hi: values?.outerDimensions?.hi,
          BreakPackQty: values?.outerDimensions?.breakPackQty,
        },
        InnerDimensions: {
          Height: values.innerDimensions?.height,
          HeightUOM: values?.innerDimensions?.heightUOM,
          Width: values.innerDimensions?.width,
          WidthUOM: values.innerDimensions?.widthUOM,
          Length: values.innerDimensions?.length,
          LengthUOM: values.innerDimensions?.lengthUOM,
          Weight: values.innerDimensions?.weight,
          WeightUOM: values.innerDimensions?.weightUOM,
          Volume: values.innerDimensions?.volume,
          VolumeUOM: values.innerDimensions?.volumeUOM,
          Ti: values?.innerDimensions?.ti,
          Hi: values?.innerDimensions?.hi,
        },
        OverridenValidations: values.overridenValidations ?? false,
      };
      store.dispatch(storeSaveResponse(null));
      store.dispatch(editTablePayload(editPayload));
    } else {
      store.dispatch(storeAddPayload({ addPayload: { ...state.values } }));
      closeModal();
    }
  };

  const clearAll = (e: any) => {
    if (isEdit) {
      consolelog("edit mode");
      setState(payload.data.editPayload);
    } else {
      setState(iInitialStateProduct);
    }
  };
  const scrollToError = () => {
    const firstError = document.querySelector(
      ".ln-c-form-group.has-error"
    ) as HTMLElement | null;

    if (firstError) {
      window.scrollTo(0, firstError.offsetTop);
      const inputField = firstError.querySelector(
        "input"
      ) as HTMLElement | null;
      inputField?.focus();
    }
  };

  return (
    <>
      <h4 className="duTypeModal-heading">
        Edit {nonDirectQueryRespnse && nonDirectQueryRespnse.selectedMenu
          ? `${nonDirectQueryRespnse.selectedMenu}`
          : `${payload.data.selectedMenu}`}
      </h4>
      <ProductEditForm
        values={values}
        onBlur={(event) => handleBlur(event.target)}
        onChange={(
          event,
          familygroup: string,
          desc: string,
          isCheckBox: boolean
        ) =>
          handleChange(
            event.target,
            event.label,
            event.value?.desc,
            isCheckBox
          )
        }
        onSubmit={(event) => handleSubmit(event)}
        onClearAll={(event) => clearAll(event)}
        onCancel={closeModal}
        errors={errors}
        dirty={dirty}
        warnings={warnings}
        submitted={submitted}
        isEdit={isEdit}
      />
    </>
  );
};

export default ProductFields;
